// A line's Feelings and Camera steps: how each face looks as the line starts, and the camera as it starts. A tap sets
// a feeling or camera marker at the line's start straight away (a marker already there is replaced). The same on a
// new line's steps and on an existing line's.
import { resolveFraming } from "../../../engine/timelineFrame";
import { useLibrary } from "../../../library/store";
import { EMOTIONS, type Emotion } from "../../../model/face";
import { findAsset } from "../../../model/library";
import { addMarker, lineById, cameraAt, feelingAt, onScreen, pictureAt, type Framing, type TimelineEpisode } from "../../../model/timeline";
import type { Camera } from "../../../model/episode";
import { FrameSvg } from "../../../render/FrameSvg";
import { Avatar, Section, cap, useIdleClock } from "../../common";
import { useTimeline } from "../TimelineLoader";
import { FreeFrameTile } from "./FreeFramePage";
import { frameWith } from "./shared";

// Who is on screen as the line starts, the speaker first (a silent line has no speaker).
export function linePeople(ep: TimelineEpisode, lineId: string): string[] {
  const line = lineById(ep, lineId);
  if (!line) return [];
  return onScreen(ep, line.start).sort((x, y) => Number(y === line.char) - Number(x === line.char));
}
export const lineHasCamera = (ep: TimelineEpisode, lineId: string) => { const b = lineById(ep, lineId); return !!b && !!pictureAt(ep, b.start); };

// One row per person: their face and name, then a chip for each feeling. `now` is how each looks at the moment.
export function FeelingRows({ people, now, onPick }: { people: string[]; now: (char: string) => Emotion; onPick: (char: string, emotion: Emotion) => void }) {
  const lib = useLibrary();
  return (
    <>
      {people.map(c => {
        const cur = now(c);
        const name = findAsset(lib, c)?.name ?? "?";
        return (
          <div key={c} className="tl-feel-row">
            <div className="tl-feel-who"><Avatar assetId={c} size={44} emotion={cur} /><b>{name}</b></div>
            <div className="tl-chips left" role="radiogroup" aria-label={`${name} looks`}>
              {EMOTIONS.map(e => (
                <button key={e} role="radio" aria-checked={cur === e} className={`tl-chip${cur === e ? " on" : ""}`}
                  onClick={() => { if (e !== cur) onPick(c, e); }}>{cap(e)}</button>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

// A framing picked from the tiles: how close, who it points at (not for the whole picture), and the camera it works out to.
export type CameraPick = { framing: Framing; on?: string; camera: Camera };

// Drawn tiles of each way to frame the picture at time t: the whole picture, a medium and a close-up of each person
// offered, and one to frame by hand. `cur` is the camera now; tapping the one it already is does nothing.
export function CameraTiles({ ep, t, people, cur, onPick }: { ep: TimelineEpisode; t: number; people: string[]; cur: { framing: Framing; on?: string; camera: Camera }; onPick: (pick: CameraPick) => void }) {
  const lib = useLibrary();
  const clock = useIdleClock();
  const options: { framing: Framing; on?: string; label: string }[] = [
    { framing: "whole", label: "Whole picture" },
    ...people.flatMap(c => {
      const n = findAsset(lib, c)?.name ?? "them";
      return [{ framing: "medium" as const, on: c, label: `${n}, medium` }, { framing: "close" as const, on: c, label: `${n}, close-up` }];
    }),
  ];
  const isOn = (o: { framing: Framing; on?: string }) => o.framing === cur.framing && (o.framing === "whole" || o.on === cur.on);
  return (
    <div className="tl-cams" role="radiogroup" aria-label="Camera">
      {options.map((o, i) => {
        const camera = resolveFraming(ep, t, o.framing, o.on);
        return (
          <button key={i} role="radio" aria-checked={isOn(o)} className={`tl-cam${isOn(o) ? " on" : ""}`}
            onClick={() => { if (!isOn(o)) onPick({ framing: o.framing, camera, ...(o.framing !== "whole" && o.on ? { on: o.on } : {}) }); }}>
            <div className="frame-box tl-cam-frame"><FrameSvg id={`tl-cam-tile-${i}`} boil={false} frame={frameWith(ep, t, clock, camera)} /></div>
            <span>{o.label}</span>
          </button>
        );
      })}
      <FreeFrameTile ep={ep} t={t} clock={clock} camera={cur.framing === "free" ? cur.camera : undefined}
        onPick={camera => onPick({ framing: "free", camera })} />
    </div>
  );
}

export function LineFeelings({ lineId }: { lineId: string }) {
  const { ep, update } = useTimeline();
  const line = lineById(ep, lineId);
  if (!line) return null;
  const at = line.start;
  const people = linePeople(ep, lineId);
  const start = (e: TimelineEpisode) => lineById(e, lineId)?.start ?? at;
  if (!people.length) return <p className="hint">Nobody is in the picture as this line starts.</p>;
  return (
    <Section title="How do they look during this line?" hint="How each face looks as this line begins.">
      <FeelingRows people={people} now={c => feelingAt(ep, c, at)} onPick={(c, e) => update(x => addMarker(x, "feeling", start(x), { char: c, emotion: e }))} />
    </Section>
  );
}

export function LineCamera({ lineId }: { lineId: string }) {
  const { ep, update } = useTimeline();
  const line = lineById(ep, lineId);
  if (!line) return null;
  const at = line.start;
  if (!pictureAt(ep, at)) return <p className="hint">There is no picture here to frame.</p>;
  const start = (e: TimelineEpisode) => lineById(e, lineId)?.start ?? at;
  return (
    <Section title="Where's the camera for this line?" hint="The framing as this line begins.">
      <CameraTiles ep={ep} t={at} people={linePeople(ep, lineId)} cur={cameraAt(ep, at)} onPick={pick => update(x => addMarker(x, "camera", start(x), pick))} />
    </Section>
  );
}
